import type { PoseJoints, PoseJointId } from './types';

const BONE_PAIRS: Array<[PoseJointId, PoseJointId]> = [
  ['head', 'neck'],
  ['neck', 'left_shoulder'],
  ['left_shoulder', 'left_elbow'],
  ['left_elbow', 'left_wrist'],
  ['neck', 'right_shoulder'],
  ['right_shoulder', 'right_elbow'],
  ['right_elbow', 'right_wrist'],
  ['neck', 'hip'],
  ['hip', 'left_hip'],
  ['left_hip', 'left_knee'],
  ['left_knee', 'left_ankle'],
  ['hip', 'right_hip'],
  ['right_hip', 'right_knee'],
  ['right_knee', 'right_ankle'],
];

const toPx = (n01: number, size: number) => n01 * size;

export function renderPoseSvg(joints: PoseJoints, size = 768): string {
  const w = size;
  const h = size;

  const lines = BONE_PAIRS.map(([a, b]) => {
    const pa = joints[a];
    const pb = joints[b];
    return {
      x1: toPx(pa.x, w),
      y1: toPx(pa.y, h),
      x2: toPx(pb.x, w),
      y2: toPx(pb.y, h),
    };
  });

  // Simple, crisp stick figure
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
  <defs>
    <linearGradient id="bg" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#0b1220"/>
      <stop offset="1" stop-color="#060912"/>
    </linearGradient>
    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="2" stdDeviation="4" flood-color="#000" flood-opacity="0.55"/>
    </filter>
  </defs>
  <rect x="0" y="0" width="${w}" height="${h}" rx="${Math.round(w * 0.04)}" fill="url(#bg)"/>
  <g stroke="#ffffff" stroke-opacity="0.9" stroke-width="${Math.max(10, Math.round(w * 0.012))}" stroke-linecap="round" filter="url(#shadow)" fill="none">
    ${lines
      .map(
        (l) =>
          `<line x1="${l.x1.toFixed(2)}" y1="${l.y1.toFixed(2)}" x2="${l.x2.toFixed(2)}" y2="${l.y2.toFixed(2)}" />`
      )
      .join('\n    ')}
  </g>
  <g fill="#7dfef1" stroke="#0b0f1a" stroke-opacity="0.9" stroke-width="${Math.max(6, Math.round(w * 0.008))}">
    ${Object.entries(joints)
      .map(([jid, p]) => {
        const cx = toPx(p.x, w);
        const cy = toPx(p.y, h);
        // Head should be the biggest joint and noticeably larger than other joints.
        const r =
          jid === 'head'
            ? Math.max(40, Math.round(w * 0.05))
            : Math.max(12, Math.round(w * 0.015));
        return `<circle cx="${cx.toFixed(2)}" cy="${cy.toFixed(2)}" r="${r}" />`;
      })
      .join('\n    ')}
  </g>
</svg>`;
}

export function svgToDataUrl(svg: string): string {
  // Base64 is the most robust across browsers and avoids escaping issues
  try {
    const encoded =
      typeof window !== 'undefined'
        ? window.btoa(unescape(encodeURIComponent(svg)))
        : Buffer.from(svg, 'utf8').toString('base64');
    return `data:image/svg+xml;base64,${encoded}`;
  } catch {
    // Fallback
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }
}


